import { cleanMessage, DOCUMENT_ACCEPT, MAX_TURN_MESSAGE_LENGTH, type MessageView } from "@procuremate/shared";
import { clsx } from "clsx";
import { CircleAlert, Paperclip, SendHorizontal, Upload } from "lucide-react";
import {
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import type { PendingTurn, TurnFailure } from "./use-conversation";

/** The counter appears once this few characters are left. */
const COUNTER_FROM = 500;
/** Within this distance of the bottom, new text keeps the view scrolled to the end. */
const STICKY_PX = 160;

function nearBottom(): boolean {
  return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - STICKY_PX;
}

/**
 * The conversation: stored messages, the turn in progress (the requester's message at once, then
 * the reply as it streams) and the composer. Text from people and the model is rendered as text.
 */
export function Conversation({
  messages,
  pending,
  failure,
  announcement,
  composer,
  onSend,
  attachments,
}: {
  messages: MessageView[];
  pending: PendingTurn | null;
  failure: TurnFailure | null;
  announcement: string;
  /** open: the requester can write; closed: the request is no longer open; hidden: not the requester. */
  composer: "open" | "closed" | "hidden";
  onSend: (text: string) => Promise<boolean>;
  /** Documents (WP-2.2): the tray above the composer and where dropped or picked files go. */
  attachments?: { tray: ReactNode; onFiles: (files: File[]) => void };
}) {
  const { t } = useTranslation();
  const end = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const canAttach = composer === "open" && attachments !== undefined;
  const hasFiles = (event: DragEvent) => event.dataTransfer.types.includes("Files");
  const stick = useRef(true);
  // A message also shown as pending (a refetch can bring it in early) is shown once.
  const shown = pending?.userMessageId ? messages.filter((m) => m.id !== pending.userMessageId) : messages;

  useEffect(() => {
    const onScroll = () => {
      stick.current = nearBottom();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Keep the newest text in view while the requester is reading at the end.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs whenever the visible text changes.
  useEffect(() => {
    if (stick.current) end.current?.scrollIntoView({ block: "end" });
  }, [shown.length, pending?.reply, pending?.text]);

  const failureText = failure && failure.kind !== "failed" ? t(`requests.conversation.${failure.kind}`) : null;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a drop target for files; the Attach button is the accessible way in.
    <div
      className="relative flex flex-col rounded-lg bg-surface shadow-card"
      onDragOver={(event) => {
        if (!canAttach || !hasFiles(event)) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(event) => {
        if (!canAttach || !hasFiles(event)) return;
        event.preventDefault();
        setDragging(false);
        attachments.onFiles([...event.dataTransfer.files]);
      }}
    >
      {dragging ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-surface/90">
          <p className="flex items-center gap-2 font-semibold text-primary">
            <Upload aria-hidden="true" className="h-5 w-5" />
            {t("requests.documents.dropHere")}
          </p>
        </div>
      ) : null}
      <h2 className="sr-only">{t("requests.conversation.title")}</h2>
      <ol className="flex flex-col gap-4 p-4 sm:p-6">
        {shown.length === 0 && !pending ? (
          <li className="rounded-md border border-border bg-bg p-4">
            <p className="font-heading text-lg font-semibold text-primary">{t("requests.conversation.introTitle")}</p>
            <p className="mt-1 max-w-prose text-fg-muted">{t("requests.conversation.intro")}</p>
          </li>
        ) : null}
        {shown.map((m) =>
          m.role === "USER" ? (
            <UserBubble key={m.id} text={m.content} />
          ) : (
            <AssistantBubble key={m.id} text={m.content} failed={m.status === "FAILED"} />
          ),
        )}
        {pending ? (
          <>
            <UserBubble text={pending.text} />
            {pending.reply ? <AssistantBubble text={pending.reply} /> : <WritingIndicator />}
          </>
        ) : null}
      </ol>
      <div ref={end} className="scroll-mb-48" />
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {composer === "open" ? (
        <div className="sticky bottom-0 rounded-b-lg border-t border-border bg-surface p-3 sm:p-4">
          {failure ? (
            <p role="alert" className="mb-2 flex items-start gap-1.5 text-sm text-danger">
              <CircleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {failureText ? `${failureText} ` : null}
                {t("requests.conversation.retryHint")}
              </span>
            </p>
          ) : null}
          {attachments?.tray}
          <Composer
            onAttach={attachments?.onFiles}
            busy={pending !== null}
            onSend={async (text) => {
              stick.current = true;
              return onSend(text);
            }}
          />
        </div>
      ) : composer === "closed" ? (
        <p className="rounded-b-lg border-t border-border bg-bg p-4 text-sm text-fg-muted">
          {t("requests.conversation.closed")}
        </p>
      ) : null}
    </div>
  );
}

function UserBubble({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <li className="flex justify-end">
      <div className="max-w-[85%] rounded-lg rounded-br-sm bg-primary px-4 py-2.5 text-on-primary">
        <p className="sr-only">{t("requests.conversation.you")}:</p>
        <p className="whitespace-pre-wrap break-words">{text}</p>
      </div>
    </li>
  );
}

function AssistantBubble({ text, failed = false }: { text: string; failed?: boolean }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-start gap-2.5">
      <img src="/brand/ts-mark.jpeg" alt="" className="mt-0.5 h-8 w-8 shrink-0 rounded-full border border-border" />
      <div
        className={clsx(
          "min-w-0 max-w-[85%] rounded-lg rounded-tl-sm border px-4 py-2.5 text-fg",
          failed ? "border-danger bg-danger-bg" : "border-border bg-bg",
        )}
      >
        <p className="sr-only">{t("requests.conversation.assistant")}:</p>
        {failed ? (
          <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-danger">
            <CircleAlert aria-hidden="true" className="h-4 w-4" />
            {t("requests.conversation.notAnswered")}
          </p>
        ) : null}
        <p className="whitespace-pre-wrap break-words">{text}</p>
      </div>
    </li>
  );
}

function WritingIndicator() {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-2.5">
      <img src="/brand/ts-mark.jpeg" alt="" className="h-8 w-8 shrink-0 rounded-full border border-border" />
      <p className="inline-flex items-center gap-2 rounded-lg rounded-tl-sm border border-border bg-bg px-4 py-2.5 text-sm text-fg-muted">
        <span aria-hidden="true" className="inline-flex gap-1">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-muted" />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-muted [animation-delay:150ms]" />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-muted [animation-delay:300ms]" />
        </span>
        {t("requests.conversation.writing")}
      </p>
    </li>
  );
}

/** Enter sends, Shift+Enter adds a line; the 4,000-character limit is counted as the server counts it. */
function Composer({
  busy,
  onSend,
  onAttach,
}: {
  busy: boolean;
  onSend: (text: string) => Promise<boolean>;
  onAttach?: (files: File[]) => void;
}) {
  const { t } = useTranslation();
  const picker = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const input = useRef<HTMLTextAreaElement>(null);
  const hintId = useId();
  const counterId = useId();
  const length = cleanMessage(text).length;
  const left = MAX_TURN_MESSAGE_LENGTH - length;
  const canSend = !busy && length > 0 && left >= 0;

  async function send(event?: FormEvent) {
    event?.preventDefault();
    if (!canSend) return;
    const message = cleanMessage(text);
    setText("");
    const delivered = await onSend(message);
    // Not answered: the message goes back into the box (unless something new was typed meanwhile).
    if (!delivered) setText((current) => current || message);
    input.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  }

  return (
    <form onSubmit={send} className="flex flex-col gap-1.5">
      <label htmlFor="composer" className="sr-only">
        {t("requests.conversation.messageLabel")}
      </label>
      <div className="flex items-end gap-2">
        {onAttach ? (
          <>
            <input
              ref={picker}
              type="file"
              multiple
              accept={DOCUMENT_ACCEPT}
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                onAttach([...(e.target.files ?? [])]);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-primary hover:bg-bg"
              aria-label={t("requests.documents.attach")}
              title={t("requests.documents.attachHint")}
              onClick={() => picker.current?.click()}
            >
              <Paperclip aria-hidden="true" className="h-4 w-4" />
            </button>
          </>
        ) : null}
        <textarea
          id="composer"
          ref={input}
          rows={2}
          value={text}
          placeholder={t("requests.conversation.placeholder")}
          aria-describedby={clsx(hintId, left < COUNTER_FROM && counterId)}
          aria-invalid={left < 0 ? true : undefined}
          className={clsx(
            "max-h-60 min-h-11 w-full min-w-0 flex-1 resize-y rounded-md border bg-surface px-3 py-2 text-base text-fg",
            left < 0 ? "border-danger" : "border-border",
          )}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button
          type="submit"
          disabled={!canSend}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-on-primary hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          <SendHorizontal aria-hidden="true" className="h-4 w-4" />
          <span className="sr-only sm:not-sr-only">{t("requests.conversation.send")}</span>
        </button>
      </div>
      <div className="flex flex-wrap justify-between gap-x-4 text-xs text-fg-muted">
        <span id={hintId} className="hidden sm:inline">
          {t("requests.conversation.hint")}
        </span>
        {left < COUNTER_FROM ? (
          <span id={counterId} aria-live="polite" className={clsx("tabular", left < 0 && "font-semibold text-danger")}>
            {left >= 0
              ? t("requests.conversation.charsLeft", { count: left })
              : t("requests.conversation.tooLong", { count: -left })}
          </span>
        ) : null}
      </div>
    </form>
  );
}
